// Device notifications on the browser side: service worker, permission and the PushSubscription.
// The server part is apps/api/src/modules/notifications/push-routes.ts.
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export type PushSupport = 'supported' | 'ios-install' | 'unsupported';

const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

/** On iPhone/iPad push exists only in the web app added to the Home Screen (iOS 16.4+), not in a Safari tab. */
export function pushSupport(): PushSupport {
  const ok = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (ok) return 'supported';
  return isIos() && !isStandalone() ? 'ios-install' : 'unsupported';
}

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register('/sw.js').catch(() => { /* push simply stays unavailable */ });
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== 'supported') return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

function keyBytes(base64url: string) {
  const b64 = base64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(base64url.length / 4) * 4, '=');
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

/** Must start from a tap: Safari only shows the permission prompt during a user gesture, so the permission
 * is requested before anything else is awaited. */
export async function subscribeDevice(publicKey: string): Promise<PushSubscription> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error(permission === 'denied' ? 'denied' : 'dismissed');
  registerServiceWorker();
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const key = keyBytes(publicKey);
  if (existing) {
    // A subscription made with another server key can't receive our pushes: replace it.
    const old = existing.options.applicationServerKey ? new Uint8Array(existing.options.applicationServerKey) : null;
    if (old && old.length === key.length && old.every((b, i) => b === key[i])) return existing;
    await existing.unsubscribe();
  }
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
}

/** On logout: the next person using this browser must not receive the previous account's notifications. */
export async function unsubscribeDevice() {
  await (await currentSubscription().catch(() => null))?.unsubscribe().catch(() => false);
}

/** Tapping a notification while the app is open moves it to the right page without a reload. */
export function usePushNavigation() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const on = (e: MessageEvent) => {
      if (e.data?.type !== 'unitly:navigate' || typeof e.data.url !== 'string') return;
      const u = new URL(e.data.url, location.origin);
      if (u.origin === location.origin) navigate(u.pathname + u.search);
    };
    navigator.serviceWorker.addEventListener('message', on);
    return () => navigator.serviceWorker.removeEventListener('message', on);
  }, [navigate]);
}
